import { useCallback, useEffect, useState } from "react";

export const installPromptKey = "__palladiumInstallPrompt";

const installedStorageKey = "__palladiumWalletAppInstalled";
const dismissedSessionKey = "__palladiumWalletInstallDismissed";

export const isStandaloneApp = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
};

export const isIOSDevice = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
};

export const rememberPwaInstalled = () => {
	try {
		window.localStorage.setItem(installedStorageKey, "1");
	} catch {
		// Some browsers can block localStorage in private mode.
	}
};

export const forgetPwaInstalled = () => {
	try {
		window.localStorage.removeItem(installedStorageKey);
	} catch {
		// Some browsers can block localStorage in private mode.
	}
};

export const rememberPwaInstallDismissed = () => {
	try {
		window.sessionStorage.setItem(dismissedSessionKey, "1");
	} catch {
		// Some browsers can block sessionStorage in private mode.
	}
};

export const forgetPwaInstallDismissed = () => {
	try {
		window.sessionStorage.removeItem(dismissedSessionKey);
	} catch {
		// Some browsers can block sessionStorage in private mode.
	}
};

export const hasPwaInstallDismissed = () => {
	try {
		return window.sessionStorage.getItem(dismissedSessionKey) === "1";
	} catch {
		return false;
	}
};

const hasStoredInstallFlag = () => {
	try {
		return window.localStorage.getItem(installedStorageKey) === "1";
	} catch {
		return false;
	}
};

const getInitialInstallStatus = () => {
	if (typeof window === "undefined") {
		return false;
	}

	return isStandaloneApp() || hasStoredInstallFlag();
};

export default function usePwaInstallStatus() {
	const [isAppInstalled, setIsAppInstalled] = useState(getInitialInstallStatus);

	const markInstalled = useCallback(() => {
		rememberPwaInstalled();
		setIsAppInstalled(true);
	}, []);

	const markNotInstalled = useCallback(() => {
		forgetPwaInstalled();
		setIsAppInstalled(false);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		let isCancelled = false;
		const standaloneMedia = window.matchMedia("(display-mode: standalone)");

		const syncStandaloneStatus = () => {
			if (isStandaloneApp()) {
				markInstalled();
			}
		};

		const handleAppInstalled = () => {
			markInstalled();
		};

		syncStandaloneStatus();

		if (typeof window.navigator.getInstalledRelatedApps === "function") {
			window.navigator.getInstalledRelatedApps()
				.then((relatedApps) => {
					if (!isCancelled && Array.isArray(relatedApps) && relatedApps.length > 0) {
						markInstalled();
					}
				})
				.catch(() => {});
		}

		standaloneMedia.addEventListener?.("change", syncStandaloneStatus);
		standaloneMedia.addListener?.(syncStandaloneStatus);
		window.addEventListener("appinstalled", handleAppInstalled);

		return () => {
			isCancelled = true;
			standaloneMedia.removeEventListener?.("change", syncStandaloneStatus);
			standaloneMedia.removeListener?.(syncStandaloneStatus);
			window.removeEventListener("appinstalled", handleAppInstalled);
		};
	}, [markInstalled]);

	return {
		isAppInstalled,
		markInstalled,
		markNotInstalled,
		setIsAppInstalled,
	};
}
